import { VStack, Box, useDisclosure, Text, Flex } from '@chakra-ui/react';
import { Link } from '@tanstack/react-router';
import { useState, type FC } from 'react';

import { CardContent } from '$/components/common/Card/CardContent';
import { Show } from '$/components/common/Flow/Show';
import { Icon, type AvailableIcons } from '$/components/common/Icon';
import { ProjectFolderContextMenu } from '$/pages/DashboardPages/pages/Projects/components/ProjectFolderContextMenu';
import { type ProjectFolder } from '$/services/usecases/projects/mapper/editorProject';
import { shortenText } from '$/utils/stringUtils';

interface Props {
  icon?: AvailableIcons;
  imageUrl?: string;
  disableContextMenu?: boolean;
  projectFolder: ProjectFolder;
  onClick?: (projectFolder: ProjectFolder) => void;
}

export const ProjectFolderItem: FC<Props> = ({
  icon,
  imageUrl,
  disableContextMenu,
  projectFolder,
  onClick,
}) => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [isHovered, setIsHovered] = useState(false);

  const folderItemContent = () => {
    if (imageUrl != null) {
      return (
        <Box
          pos='relative'
          overflow='hidden'
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Show when={!disableContextMenu && (isHovered || isOpen)}>
            <ProjectFolderContextMenu
              isOpen={isOpen}
              onClose={onClose}
              onOpen={onOpen}
              projectFolder={projectFolder}
            />
          </Show>
          <CardContent
            label={projectFolder.name}
            bg='transparent'
            image={imageUrl}
          />
        </Box>
      );
    }

    const defaultIcon =
      projectFolder.id === '_schoolclass' ? 'users' : 'folder';

    const badge = () => {
      if (projectFolder.id === '_schoolclass') {
        return (
          <Flex
            pos='absolute'
            top='7%'
            left='-40%'
            justify='center'
            w='100%'
            bg='primaryButton.background'
            transform='rotate(-45deg)'
            userSelect='none'
          >
            <Text p='1' color='reverseText' fontSize='xs' fontWeight='bold'>
              SCHOOL
            </Text>
          </Flex>
        );
      }
      return null;
    };

    return (
      <VStack
        layerStyle='WelcomeCard'
        pos='relative'
        overflow='hidden'
        bg='bodyBackground'
        cursor='pointer'
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {badge()}
        <Show when={!disableContextMenu && (isHovered || isOpen)}>
          <ProjectFolderContextMenu
            isOpen={isOpen}
            onClose={onClose}
            onOpen={onOpen}
            projectFolder={projectFolder}
          />
        </Show>
        <Icon icon={icon ?? defaultIcon} boxSize='40px' />
        <Box
          maxW='full'
          px='3'
          textOverflow='ellipsis'
          title={projectFolder.name}
        >
          {shortenText(projectFolder.name, 45)}
        </Box>
      </VStack>
    );
  };

  if (onClick != null) {
    return (
      <Box as='button' onClick={() => onClick(projectFolder)}>
        {folderItemContent()}
      </Box>
    );
  }

  return (
    <Link to='/projects' search={() => ({ folderId: projectFolder.id })}>
      {folderItemContent()}
    </Link>
  );
};
